import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import domiCarIcon from '../../lib/libraries/extensions/domiCar/domi-car.svg';
import domiAiRoundIcon from '../../lib/libraries/extensions/domiAiRound/domi-ai-round.svg';
import microbitIcon from '../../lib/libraries/extensions/microbit/microbit.png';
import intlShape from '../../lib/intlShape.js';
import {getCompatibleConnections, getHardwareDevice} from '../../lib/hardware-devices.js';

import styles from './hardware-device-pane.css';

const messages = defineMessages({
    addDevice: {
        id: 'gui.hardwareDevicePane.addDevice',
        defaultMessage: '添加设备',
        description: 'Button to open the hardware extension library'
    },
    address: {
        id: 'gui.hardwareDevicePane.address',
        defaultMessage: '小车地址',
        description: 'Label for the Domi Car network address'
    },
    roundAddress: {
        id: 'gui.hardwareDevicePane.roundAddress',
        defaultMessage: '圆屏地址',
        description: 'Label for the Domi AI round screen network address'
    },
    bluetooth: {
        id: 'gui.hardwareDevicePane.bluetooth',
        defaultMessage: '蓝牙',
        description: 'Bluetooth hardware connection option'
    },
    connectBluetooth: {
        id: 'gui.hardwareDevicePane.connectBluetooth',
        defaultMessage: '连接蓝牙',
        description: 'Button to connect a Bluetooth hardware device'
    },
    connectCar: {
        id: 'gui.hardwareDevicePane.connectCar',
        defaultMessage: '连接小车',
        description: 'Button to connect a Domi Car over Wi-Fi'
    },
    connectRound: {
        id: 'gui.hardwareDevicePane.connectRound',
        defaultMessage: '连接圆屏',
        description: 'Button to connect a Domi AI round screen'
    },
    connected: {
        id: 'gui.hardwareDevicePane.connected',
        defaultMessage: '设备已连接',
        description: 'Hardware connected status'
    },
    connecting: {
        id: 'gui.hardwareDevicePane.connecting',
        defaultMessage: '正在连接…',
        description: 'Hardware connecting status'
    },
    connectionHeading: {
        id: 'gui.hardwareDevicePane.connectionHeading',
        defaultMessage: '连接方式',
        description: 'Heading above hardware connection options'
    },
    emptyDescription: {
        id: 'gui.hardwareDevicePane.emptyDescription',
        defaultMessage: '从扩展库添加 Domi 小车或 micro:bit 后，就能在这里连接和上传程序。',
        description: 'Help text when the project has no hardware extensions'
    },
    emptyTitle: {
        id: 'gui.hardwareDevicePane.emptyTitle',
        defaultMessage: '还没有添加硬件设备',
        description: 'Title when the project has no hardware extensions'
    },
    firmwareVersion: {
        id: 'gui.hardwareDevicePane.firmwareVersion',
        defaultMessage: '固件 {version}',
        description: 'Domi Car firmware version shown after device verification'
    },
    modeHeading: {
        id: 'gui.hardwareDevicePane.modeHeading',
        defaultMessage: '编程模式',
        description: 'Heading above hardware programming modes'
    },
    notConnected: {
        id: 'gui.hardwareDevicePane.notConnected',
        defaultMessage: '等待连接设备',
        description: 'Hardware disconnected status'
    },
    online: {
        id: 'gui.hardwareDevicePane.online',
        defaultMessage: '在线',
        description: 'Live hardware programming mode'
    },
    onlineHelp: {
        id: 'gui.hardwareDevicePane.onlineHelp',
        defaultMessage: '运行 Scratch 积木时，指令会立即发送到设备。',
        description: 'Help text for live hardware programming mode'
    },
    openBurnCenter: {
        id: 'gui.hardwareDevicePane.openBurnCenter',
        defaultMessage: '打开烧录中心',
        description: 'Button to open the selected hardware burn center'
    },
    openDeviceCenter: {
        id: 'gui.hardwareDevicePane.openDeviceCenter',
        defaultMessage: '打开设备中心',
        description: 'Button to open the Domi Device Center'
    },
    reconnectBluetooth: {
        id: 'gui.hardwareDevicePane.reconnectBluetooth',
        defaultMessage: '管理蓝牙连接',
        description: 'Button to manage a connected Bluetooth hardware device'
    },
    reconnectCar: {
        id: 'gui.hardwareDevicePane.reconnectCar',
        defaultMessage: '重新连接',
        description: 'Button to reconnect a Domi Car over Wi-Fi'
    },
    serial: {
        id: 'gui.hardwareDevicePane.serial',
        defaultMessage: 'USB 串口',
        description: 'USB serial hardware connection option'
    },
    serialHelp: {
        id: 'gui.hardwareDevicePane.serialHelp',
        defaultMessage: '用于安装小车固件和配置 Wi-Fi，日常控制使用 Wi-Fi。',
        description: 'Help text for Domi Car USB serial setup'
    },
    roundUsbHelp: {
        id: 'gui.hardwareDevicePane.roundUsbHelp',
        defaultMessage: '通过 USB 数据线实时控制圆屏，浏览器会请求串口权限。',
        description: 'Help text for Domi AI round screen USB control'
    },
    roundWifiHelp: {
        id: 'gui.hardwareDevicePane.roundWifiHelp',
        defaultMessage: '先在圆屏打开 Domi 配网并连接同一网络，再填写圆屏显示的 IP 地址。',
        description: 'Help text for Domi AI round screen Wi-Fi control'
    },
    upload: {
        id: 'gui.hardwareDevicePane.upload',
        defaultMessage: '上传',
        description: 'Offline hardware programming mode'
    },
    uploadHelp: {
        id: 'gui.hardwareDevicePane.uploadHelp',
        defaultMessage: '把当前项目编译并写入设备，脱离 Scratch 后也能运行。',
        description: 'Help text for offline hardware programming mode'
    },
    usb: {
        id: 'gui.hardwareDevicePane.usb',
        defaultMessage: 'USB',
        description: 'USB hardware connection option'
    },
    verifiedCar: {
        id: 'gui.hardwareDevicePane.verifiedCar',
        defaultMessage: '已验证为 Domi 小车',
        description: 'Label shown after the Wi-Fi endpoint is verified as a Domi Car'
    },
    verifiedRound: {
        id: 'gui.hardwareDevicePane.verifiedRound',
        defaultMessage: '已验证为 Domi AI 圆屏',
        description: 'Label shown after a Domi AI round screen is verified'
    },
    wifi: {
        id: 'gui.hardwareDevicePane.wifi',
        defaultMessage: 'Wi-Fi',
        description: 'Wi-Fi hardware connection option'
    }
});

const DEVICE_ICONS = {
    domiCar: domiCarIcon,
    domiAiRound: domiAiRoundIcon,
    microbit: microbitIcon
};

const CONNECTION_MESSAGES = {
    bluetooth: messages.bluetooth,
    serial: messages.serial,
    usb: messages.usb,
    wifi: messages.wifi
};

const CONNECTION_GLYPHS = {
    bluetooth: 'ᛒ',
    serial: '↯',
    usb: '↯',
    wifi: '◉'
};

const HardwareDevicePane = ({
    address,
    connected,
    connecting,
    connectionType = null,
    deviceInfo = null,
    devices,
    intl,
    mode,
    selectedDeviceId = null,
    statusMessage = '',
    onAddDevice,
    onAddressChange,
    onConnectionTypeChange,
    onModeChange,
    onPrimaryAction,
    onSelectDevice
}) => {
    const selectedDevice = getHardwareDevice(selectedDeviceId);
    const compatibleConnections = getCompatibleConnections(selectedDevice, mode);
    const isDomiNetworkDevice = selectedDeviceId === 'domiCar' || selectedDeviceId === 'domiAiRound';
    const showAddressField = isDomiNetworkDevice && connectionType === 'wifi';
    const showDeviceMetadata = isDomiNetworkDevice && connected && deviceInfo;

    if (!devices.length || !selectedDevice) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>＋</div>
                <strong>{intl.formatMessage(messages.emptyTitle)}</strong>
                <p>{intl.formatMessage(messages.emptyDescription)}</p>
                <button
                    className={styles.primaryButton}
                    type="button"
                    onClick={onAddDevice}
                >
                    {intl.formatMessage(messages.addDevice)}
                </button>
            </div>
        );
    }

    let primaryMessage = messages.connectCar;
    if (selectedDeviceId === 'microbit') {
        primaryMessage = mode === 'upload' ? messages.openBurnCenter :
            (connected ? messages.reconnectBluetooth : messages.connectBluetooth);
    } else if (selectedDeviceId === 'domiAiRound') {
        primaryMessage = connected ? messages.reconnectCar : messages.connectRound;
    } else if (connectionType === 'serial') {
        primaryMessage = messages.openDeviceCenter;
    } else if (mode === 'upload') {
        primaryMessage = messages.openBurnCenter;
    } else if (connected) {
        primaryMessage = messages.reconnectCar;
    }

    return (
        <div className={styles.pane}>
            <aside className={styles.deviceRail}>
                <div className={styles.deviceList}>
                    {devices.map(device => (
                        <button
                            aria-pressed={device.id === selectedDeviceId}
                            className={classNames(styles.deviceCard, {
                                [styles.selectedDevice]: device.id === selectedDeviceId
                            })}
                            key={device.id}
                            type="button"
                            value={device.id}
                            onClick={onSelectDevice}
                        >
                            <img
                                alt=""
                                src={DEVICE_ICONS[device.id]}
                            />
                            <span>{device.name}</span>
                        </button>
                    ))}
                </div>
                <button
                    className={styles.addButton}
                    type="button"
                    onClick={onAddDevice}
                >
                    <span aria-hidden="true">＋</span>
                    {intl.formatMessage(messages.addDevice)}
                </button>
            </aside>

            <section className={styles.details}>
                <div
                    className={styles.detailsScroll}
                    data-testid="hardware-device-scroll"
                >
                    <div className={styles.deviceTitle}>
                        <img
                            alt=""
                            src={DEVICE_ICONS[selectedDeviceId]}
                        />
                        <div>
                            <strong>{selectedDevice.name}</strong>
                            <span className={connected ? styles.connected : styles.disconnected}>
                                {connecting ? intl.formatMessage(messages.connecting) :
                                    (connected ? intl.formatMessage(messages.connected) :
                                        intl.formatMessage(messages.notConnected))}
                            </span>
                        </div>
                    </div>

                    <div className={styles.controlGroup}>
                        <span className={styles.groupLabel}>{intl.formatMessage(messages.connectionHeading)}</span>
                        <div className={styles.optionGrid}>
                            {compatibleConnections.map(connection => (
                                <button
                                    aria-pressed={connection.id === connectionType}
                                    className={classNames(styles.optionButton, {
                                        [styles.selectedOption]: connection.id === connectionType
                                    })}
                                    key={connection.id}
                                    type="button"
                                    value={connection.id}
                                    onClick={onConnectionTypeChange}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={styles.connectionGlyph}
                                    >
                                        {CONNECTION_GLYPHS[connection.id]}
                                    </span>
                                    {intl.formatMessage(CONNECTION_MESSAGES[connection.id])}
                                </button>
                            ))}
                        </div>
                    </div>

                    {selectedDevice.modes.length > 1 ? <div className={styles.controlGroup}>
                        <span className={styles.groupLabel}>{intl.formatMessage(messages.modeHeading)}</span>
                        <div className={styles.modeSwitch}>
                            <button
                                aria-pressed={mode === 'online'}
                                className={classNames({[styles.activeMode]: mode === 'online'})}
                                type="button"
                                value="online"
                                onClick={onModeChange}
                            >
                                {intl.formatMessage(messages.online)}
                            </button>
                            <button
                                aria-pressed={mode === 'upload'}
                                className={classNames({[styles.activeMode]: mode === 'upload'})}
                                type="button"
                                value="upload"
                                onClick={onModeChange}
                            >
                                {intl.formatMessage(messages.upload)}
                            </button>
                        </div>
                        <p className={styles.helpText}>
                            {intl.formatMessage(mode === 'online' ? messages.onlineHelp : messages.uploadHelp)}
                        </p>
                    </div> : null}

                    {showAddressField ? (
                        <label className={styles.addressField}>
                            <span>{intl.formatMessage(selectedDeviceId === 'domiAiRound' ?
                                messages.roundAddress : messages.address)}</span>
                            <input
                                type="text"
                                value={address}
                                onChange={onAddressChange}
                            />
                        </label>
                    ) : null}

                    {selectedDeviceId === 'domiAiRound' && connectionType === 'wifi' ? (
                        <p className={styles.helpText}>{intl.formatMessage(messages.roundWifiHelp)}</p>
                    ) : null}

                    {showDeviceMetadata ? (
                        <div className={styles.deviceMetadata}>
                            <strong>{intl.formatMessage(selectedDeviceId === 'domiAiRound' ?
                                messages.verifiedRound : messages.verifiedCar)}</strong>
                            <span>
                                {deviceInfo.firmwareVersion ? intl.formatMessage(messages.firmwareVersion, {
                                    version: deviceInfo.firmwareVersion
                                }) : null}
                                {deviceInfo.firmwareVersion && deviceInfo.ip ? ' · ' : null}
                                {deviceInfo.ip || null}
                            </span>
                        </div>
                    ) : null}

                    {selectedDeviceId === 'domiCar' && connectionType === 'serial' ? (
                        <p className={styles.helpText}>{intl.formatMessage(messages.serialHelp)}</p>
                    ) : null}

                    {selectedDeviceId === 'domiAiRound' && connectionType === 'usb' ? (
                        <p className={styles.helpText}>{intl.formatMessage(messages.roundUsbHelp)}</p>
                    ) : null}

                    {statusMessage ? <div className={styles.statusMessage}>{statusMessage}</div> : null}
                </div>

                <button
                    className={styles.primaryButton}
                    disabled={connecting || (
                        isDomiNetworkDevice &&
                        connectionType === 'wifi' && !address.trim()
                    )}
                    type="button"
                    onClick={onPrimaryAction}
                >
                    {connecting ? intl.formatMessage(messages.connecting) : intl.formatMessage(primaryMessage)}
                </button>
            </section>
        </div>
    );
};

HardwareDevicePane.propTypes = {
    address: PropTypes.string.isRequired,
    connected: PropTypes.bool.isRequired,
    connecting: PropTypes.bool.isRequired,
    connectionType: PropTypes.string,
    deviceInfo: PropTypes.shape({
        firmwareVersion: PropTypes.string,
        ip: PropTypes.string
    }),
    devices: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    intl: intlShape.isRequired,
    mode: PropTypes.oneOf(['online', 'upload']).isRequired,
    selectedDeviceId: PropTypes.string,
    statusMessage: PropTypes.string,
    onAddDevice: PropTypes.func.isRequired,
    onAddressChange: PropTypes.func.isRequired,
    onConnectionTypeChange: PropTypes.func.isRequired,
    onModeChange: PropTypes.func.isRequired,
    onPrimaryAction: PropTypes.func.isRequired,
    onSelectDevice: PropTypes.func.isRequired
};

export {HardwareDevicePane};
export default injectIntl(HardwareDevicePane);
